/**
 * ThemePicker — network-style personal theme toggle for the Community page.
 *
 * Persists two localStorage keys:
 *   theme:  "light" | "dark"       (applied via <html class="dark">)
 *   accent: preset id (see PRESETS) — writes --primary + --ring HSL vars
 *
 * The values are also applied on every mount so users get their choice back
 * after a full page refresh.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Palette, Sun, Moon, Check } from 'lucide-react';

const PRESETS = [
  { id: 'blue',    label: 'Electric Blue', hsl: '217 87% 55%',  swatch: '#2B73F0' },
  { id: 'navy',    label: 'Deep Navy',     hsl: '218 65% 17%',  swatch: '#0F2447' },
  { id: 'sky',     label: 'Ice Blue',      hsl: '209 94% 66%',  swatch: '#5AAEF9' },
  { id: 'emerald', label: 'Emerald',       hsl: '158 74% 40%',  swatch: '#1DAB77' },
  { id: 'violet',  label: 'Violet',        hsl: '265 84% 60%',  swatch: '#8B5CF6' },
];

const applyAccent = (id) => {
  const preset = PRESETS.find((p) => p.id === id) || PRESETS[0];
  const root = document.documentElement;
  root.style.setProperty('--primary', preset.hsl);
  root.style.setProperty('--ring', preset.hsl);
};

const applyTheme = (mode) => {
  const root = document.documentElement;
  if (mode === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
};

// Boot: apply saved prefs on first import so the whole app inherits them.
try {
  applyTheme(localStorage.getItem('theme') || 'light');
  applyAccent(localStorage.getItem('accent') || 'amber');
} catch { /* SSR / private mode — silent */ }

const ThemePicker = () => {
  const [open, setOpen]     = useState(false);
  const [theme, setTheme]   = useState(() => localStorage.getItem('theme') || 'light');
  const [accent, setAccent] = useState(() => localStorage.getItem('accent') || 'amber');
  const wrapRef = useRef(null);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pickTheme = (mode) => {
    setTheme(mode);
    localStorage.setItem('theme', mode);
    applyTheme(mode);
  };

  const pickAccent = (id) => {
    setAccent(id);
    localStorage.setItem('accent', id);
    applyAccent(id);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="theme-picker-btn"
        aria-label="Customize theme"
        className="inline-flex items-center gap-1.5 rounded-full h-9 px-3 text-xs font-bold border border-border bg-card text-foreground/80 hover:text-foreground hover:border-primary/60 transition-colors"
      >
        <Palette className="w-3.5 h-3.5" />
        Theme
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 z-40 w-64 rounded-xl border border-border bg-popover shadow-xl p-3 space-y-3"
          data-testid="theme-picker-panel"
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">Appearance</div>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                onClick={() => pickTheme('light')}
                data-testid="theme-light"
                className={`inline-flex items-center justify-center gap-1.5 rounded-lg h-9 text-xs font-bold border transition-colors ${
                  theme === 'light'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-foreground/70 hover:border-primary/40'
                }`}
              >
                <Sun className="w-3.5 h-3.5" /> Light
              </button>
              <button
                type="button"
                onClick={() => pickTheme('dark')}
                data-testid="theme-dark"
                className={`inline-flex items-center justify-center gap-1.5 rounded-lg h-9 text-xs font-bold border transition-colors ${
                  theme === 'dark'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-foreground/70 hover:border-primary/40'
                }`}
              >
                <Moon className="w-3.5 h-3.5" /> Dark
              </button>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">Accent color</div>
            <div className="grid grid-cols-4 gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pickAccent(p.id)}
                  data-testid={`accent-${p.id}`}
                  aria-label={`Accent ${p.label}`}
                  className={`relative h-10 rounded-lg border-2 transition-all ${
                    accent === p.id ? 'border-foreground scale-105' : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: p.swatch }}
                >
                  {accent === p.id && (
                    <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow" strokeWidth={3} />
                  )}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1.5 text-center">
              Your choice is saved on this device.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemePicker;
